import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Image,
  Input,
  List,
  Result,
  Segmented,
  Skeleton,
  Space,
  Tag,
  Typography
} from "antd";
import { FilePdfOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { SignatureForm } from "../workReports/SignDialog";
import "../workReports/workReports.css";
import { formatDateTime, formatDay } from "../workReports/format";
import {
  getPublicAcceptance,
  publicPdfUrl,
  publicPhotoUrl,
  refusePublicAcceptance,
  signPublicAcceptance
} from "./api";
import { basisLabels } from "./labels";
import "./warranty.css";

/** The customer's page behind the acceptance link: read the protocol, accept (sign) or refuse. */
const PublicAcceptance = () => {
  const { t, i18n } = useTranslation("dashboard");
  const { token } = useParams();
  const domain = new URLSearchParams(useLocation().search).get("domain") || "";
  const [state, setState] = useState({ loading: true });
  const [mode, setMode] = useState("accept");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [refusal] = Form.useForm();

  useEffect(() => {
    getPublicAcceptance(token, domain)
      .then(res => setState({ loading: false, view: res.data.data, expiresAt: res.data.expiresAt }))
      .catch(() => setState({ loading: false, failed: true }));
  }, [token, domain]);

  const send = async fn => {
    setBusy(true);
    setError("");
    try {
      const res = await fn();
      setState(prev => ({ ...prev, view: res.data.data, done: true }));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err?.response?.data?.errorMessage || t("Could not be saved. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const v = state.view;
  const open = v && ["draft", "requested"].includes(v.status);
  const pdf = (
    <Button
      icon={<FilePdfOutlined />}
      href={publicPdfUrl(token, domain)}
      target="_blank"
      rel="noopener noreferrer"
    >
      {t("Download PDF")}
    </Button>
  );

  return (
    <div className="warranty-page wty-public" dir={i18n.dir?.()}>
      <div className="wty-public-inner">
        {state.loading ? (
          <Card>
            <Skeleton active paragraph={{ rows: 10 }} />
          </Card>
        ) : state.failed ? (
          <Card>
            <Result
              status="warning"
              title={t("This link is not valid or has expired.")}
              subTitle={t("Please ask the company for a new link.")}
            />
          </Card>
        ) : (
          <>
            {v.seller?.name && (
              <div className="wty-public-company">
                {v.seller.logo ? <img src={v.seller.logo} alt="" /> : null}
                <Typography.Text strong>{v.seller.name}</Typography.Text>
              </div>
            )}
            {!open && (
              <Result
                status={v.status === "accepted" ? "success" : "info"}
                title={
                  v.status === "accepted"
                    ? t("Thank you, the acceptance is signed.")
                    : t("The refusal was sent to the company.")
                }
                extra={pdf}
              />
            )}
            <Card className="mb-3" title={`${t("Acceptance protocol")} ${v.number}`}>
              <Descriptions size="small" column={{ xs: 1, md: 2 }}>
                <Descriptions.Item label={t("Date")}>
                  {formatDay(v.date)}
                  {v.time ? `, ${v.time}` : ""}
                </Descriptions.Item>
                <Descriptions.Item label={t("Place")}>{v.place}</Descriptions.Item>
                {v.project && (
                  <Descriptions.Item label={t("Project")}>{v.project.name}</Descriptions.Item>
                )}
                <Descriptions.Item label={t("Contract basis")}>
                  {basisLabels(t)[v.contractBasis]}
                </Descriptions.Item>
                {v.scope === "partial" && (
                  <Descriptions.Item label={t("Scope")}>
                    {t("Partial acceptance")}
                  </Descriptions.Item>
                )}
              </Descriptions>
              <Typography.Title level={5}>{t("Accepted work")}</Typography.Title>
              <div className="wty-pre mb-3">{v.workDescription}</div>
              <Typography.Title level={5}>{t("Defects")}</Typography.Title>
              {v.defects.length ? (
                <List
                  size="small"
                  dataSource={v.defects}
                  renderItem={(d, i) => (
                    <List.Item>
                      <div>
                        <Typography.Text strong>{i + 1}. </Typography.Text>
                        {d.description} {d.essential && <Tag color="error">{t("Essential")}</Tag>}
                        <div className="wty-small">
                          {[
                            d.location,
                            d.deadline && t("until {{date}}", { date: formatDay(d.deadline) })
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                        {d.photos.length > 0 && (
                          <Space wrap className="wty-photos">
                            {d.photos.map(p => (
                              <Image
                                key={p._id}
                                width={80}
                                height={80}
                                src={publicPhotoUrl(token, domain, p._id)}
                                alt=""
                              />
                            ))}
                          </Space>
                        )}
                      </div>
                    </List.Item>
                  )}
                />
              ) : (
                <Typography.Paragraph type="secondary">
                  {t("No defects found")}
                </Typography.Paragraph>
              )}
              {(v.reservations.contractPenalty ||
                v.reservations.knownDefects ||
                v.reservations.note) && (
                <>
                  <Typography.Title level={5}>{t("Reservations of the customer")}</Typography.Title>
                  <ul>
                    {v.reservations.contractPenalty && <li>{t("Contractual penalty reserved")}</li>}
                    {v.reservations.knownDefects && (
                      <li>{t("Rights for the listed known defects reserved")}</li>
                    )}
                    {v.reservations.note && <li>{v.reservations.note}</li>}
                  </ul>
                </>
              )}
            </Card>
            {open && !state.done && (
              <Card className="mb-3" title={t("Your decision")}>
                <Segmented
                  className="mb-3"
                  value={mode}
                  onChange={setMode}
                  options={[
                    { value: "accept", label: t("Accept") },
                    { value: "refuse", label: t("Refuse acceptance") }
                  ]}
                />
                {mode === "accept" ? (
                  <>
                    <Typography.Paragraph type="secondary">
                      {t(
                        "With your signature you accept the work, subject to the defects and reservations listed above."
                      )}
                    </Typography.Paragraph>
                    <div className="work-reports-page">
                      <SignatureForm
                        defaultName={v.customer?.contactName || v.customer?.name || ""}
                        showCopy={false}
                        showLocation={false}
                        submitting={busy}
                        onSubmit={values =>
                          send(() =>
                            signPublicAcceptance(token, domain, {
                              name: values.name,
                              image: values.image
                            })
                          )
                        }
                      />
                    </div>
                  </>
                ) : (
                  <Form
                    form={refusal}
                    layout="vertical"
                    onFinish={values => send(() => refusePublicAcceptance(token, domain, values))}
                  >
                    <Form.Item
                      name="name"
                      label={t("Your name")}
                      rules={[{ required: true, min: 2 }]}
                    >
                      <Input maxLength={120} />
                    </Form.Item>
                    <Form.Item
                      name="reason"
                      label={t("Which defects prevent the acceptance?")}
                      rules={[{ required: true, min: 3 }]}
                    >
                      <Input.TextArea autoSize={{ minRows: 3, maxRows: 8 }} maxLength={2000} />
                    </Form.Item>
                    <Button danger type="primary" htmlType="submit" loading={busy}>
                      {t("Refuse acceptance")}
                    </Button>
                  </Form>
                )}
                {error && <Alert className="mt-3" type="error" showIcon message={error} />}
                {state.expiresAt && (
                  <Typography.Paragraph type="secondary" className="mt-3">
                    {t("Valid until {{date}}", {
                      date: formatDateTime(state.expiresAt, i18n.language)
                    })}
                  </Typography.Paragraph>
                )}
              </Card>
            )}
            {open && <div className="mb-3">{pdf}</div>}
          </>
        )}
      </div>
    </div>
  );
};

export default PublicAcceptance;
